import React, { useRef, useState } from 'react'
import useSWR from 'swr'
import { deleteImage, fetchCompetitionLevels, uploadImage } from '../services/api'
import { Button } from './Button'
import { Modal } from './Modal'
import listTableStyles from '../styles/list-table.module.scss'
import pageStyles from '../styles/page.module.scss'
import styles from './CompetitionLevelLogosModal.module.scss'

interface CompetitionLevelLogo {
  image_id: string
  url: string | null
  type_code: string
}

interface CompetitionLevelItem {
  tc_competition_level_id: string
  ext_level: string | null
  tc_level: string | null
  tc_display_name: string
  logo_image: CompetitionLevelLogo | null
}

interface CompetitionLevelsResponse {
  competition_levels: CompetitionLevelItem[]
  timestamp: string
}

interface CompetitionLevelLogosModalProps {
  isOpen: boolean
  onClose: () => void
}

interface CompetitionLevelLogoRowProps {
  level: CompetitionLevelItem
  isUploading: boolean
  isDeleting: boolean
  onUpload: (level: CompetitionLevelItem, file: File) => Promise<void>
  onDelete: (level: CompetitionLevelItem) => Promise<void>
}

const getErrorMessage = (error: unknown, fallback: string) => {
  return error instanceof Error ? error.message : fallback
}

const CompetitionLevelLogoRow: React.FC<CompetitionLevelLogoRowProps> = ({
  level,
  isUploading,
  isDeleting,
  onUpload,
  onDelete,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleSelectFile = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    await onUpload(level, file)
    event.target.value = ''
  }

  return (
    <div className={`${listTableStyles.tableRow} ${styles.tableRow}`}>
      <div className={listTableStyles.cellPrimary}>
        <p className={listTableStyles.primaryText}>{level.tc_display_name}</p>
      </div>

      <div className={styles.logoCell}>
        {level.logo_image?.url ? (
          <img
            src={level.logo_image.url}
            alt={`${level.tc_display_name} logo`}
            className={styles.logoThumbnail}
          />
        ) : (
          <span className={styles.emptyState}>No logo</span>
        )}
      </div>

      <div className={`${listTableStyles.cellActions} ${styles.actionsCell}`}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className={styles.fileInput}
          onChange={handleFileChange}
        />

        <Button
          variant="secondary"
          size="small"
          onClick={handleSelectFile}
          disabled={isUploading || isDeleting}
        >
          {level.logo_image ? 'Replace' : 'Upload'}
        </Button>

        {level.logo_image && (
          <Button
            variant="danger"
            size="small"
            onClick={() => onDelete(level)}
            disabled={isUploading || isDeleting}
          >
            Delete
          </Button>
        )}
      </div>
    </div>
  )
}

export const CompetitionLevelLogosModal: React.FC<CompetitionLevelLogosModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [activeLevelId, setActiveLevelId] = useState<string | null>(null)
  const [activeAction, setActiveAction] = useState<'upload' | 'delete' | null>(null)
  const [actionMessage, setActionMessage] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const { data, error, isLoading, mutate } = useSWR<CompetitionLevelsResponse>(
    isOpen ? 'competition-levels' : null,
    fetchCompetitionLevels,
    { revalidateOnFocus: false }
  )

  const competitionLevels = data?.competition_levels || []

  const handleUpload = async (level: CompetitionLevelItem, file: File) => {
    setActiveLevelId(level.tc_competition_level_id)
    setActiveAction('upload')
    setActionError(null)
    setActionMessage(null)

    try {
      await uploadImage('competitionLevel', level.tc_competition_level_id, file, {
        alt_text: `${level.tc_display_name} logo`,
        image_type: 'competition_level_logo',
      })
      await mutate()
      setActionMessage(`${level.tc_display_name} logo updated.`)
    } catch (uploadError: unknown) {
      setActionError(getErrorMessage(uploadError, 'Failed to upload logo.'))
    } finally {
      setActiveLevelId(null)
      setActiveAction(null)
    }
  }

  const handleDelete = async (level: CompetitionLevelItem) => {
    if (!level.logo_image) {
      return
    }

    const confirmed = window.confirm(
      `Delete the logo for ${level.tc_display_name}?`
    )
    if (!confirmed) {
      return
    }

    setActiveLevelId(level.tc_competition_level_id)
    setActiveAction('delete')
    setActionError(null)
    setActionMessage(null)

    try {
      await deleteImage(
        'competitionLevel',
        level.tc_competition_level_id,
        level.logo_image.image_id
      )
      await mutate()
      setActionMessage(`${level.tc_display_name} logo deleted.`)
    } catch (deleteError: unknown) {
      setActionError(getErrorMessage(deleteError, 'Failed to delete logo.'))
    } finally {
      setActiveLevelId(null)
      setActiveAction(null)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Tournament Level Logos"
      size="large"
    >
      <div className={styles.intro}>
        Shared logos used across tournaments by level
      </div>

      {actionError && (
        <div className={pageStyles.error}>
          <p>{actionError}</p>
        </div>
      )}

      {actionMessage && (
        <div className={styles.successMessage}>{actionMessage}</div>
      )}

      {error ? (
        <div className={pageStyles.error}>
          <h2>Error loading competition levels</h2>
          <p>{(error as Error).message}</p>
        </div>
      ) : isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading level logos...</h3>
          <p>Please wait while we fetch competition levels.</p>
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <div className={`${listTableStyles.tableHeader} ${styles.tableHeader}`}>
            <div>Level</div>
            <div>Logo</div>
            <div>Actions</div>
          </div>

          <div className={listTableStyles.tableBody}>
            {competitionLevels.map((level) => (
              <CompetitionLevelLogoRow
                key={level.tc_competition_level_id}
                level={level}
                isUploading={
                  activeLevelId === level.tc_competition_level_id &&
                  activeAction === 'upload'
                }
                isDeleting={
                  activeLevelId === level.tc_competition_level_id &&
                  activeAction === 'delete'
                }
                onUpload={handleUpload}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}
    </Modal>
  )
}
